import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useAppStore } from '../../store/appStore.tsx';
import { crudReportesApi } from '../../actions/reportesApi';
import {
    Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, TextField, Button
} from '@mui/material';
import Chart from 'react-apexcharts';
import { Stack } from '@mui/system';
import {
    CE_ESTADOS_ALARMA, CE_ESTADOS_ZONA, CE_HABILITACIONES_ZONA,
    accionesAlarma, estadosAlarma, mediosAlarma
} from './utils/definiciones.js';
import {
    formatearFecha, generarDatosCombinados, generarOpcionesCombinadas,
    generarOpcionesEstados, generarOpcionesPuertas, generarOpcionesSalac, generarOpcionesZonas, generarSerieEstado,
    generarSeriesPuertas,
    generarSeriesSalac,
    generarSeriesZonas, procesarTramaZonas
} from './utils/funciones.js';
import dayjs from 'dayjs';

const formatearCelular = (numero) => {
    // Verificamos que exista el número para evitar errores si viene undefined o null
    if (!numero) return "---";

    // Reemplaza usando grupos de captura: (+591) (759) (15082)
    return numero.replace(/^(\+\d{3})(\d{3})(\d+)$/, '$1 $2-$3');
};

const ReporteEstado = () => {

    const currentUser = useAppStore(appStore => appStore.currentUser);
    const { device } = useAppStore(appStore => appStore);
    //const { deviceUser } = useAppStore(appStore => appStore);

    const [isLoading, setIsLoading] = useState(true);
    const [value, setValue] = React.useState('1');
    const [registros, setRegistros] = useState([]);
    const [eventos, setEventos] = useState([]);
    const [registrosAccesos, setRegistrosAccesos] = useState([]);
    const [zonaConfig, setZonaConfig] = useState([]);
    const [salacConfig, setSalacConfig] = useState([]);
    const [accesosConfig, setAccesosConfig] = useState([]);

    // Nuevos estados para las fechas y errores
    // const [fechaDesde, setFechaDesde] = useState(dayjs().subtract(1, 'day').format('YYYY-MM-DD'));
    const [fechaDesde, setFechaDesde] = useState(dayjs().format('YYYY-MM-DD'));
    const [fechaHasta, setFechaHasta] = useState(dayjs().format('YYYY-MM-DD'));
    const [errorFechas, setErrorFechas] = useState('');

    /* Obtenemos el tipo de dispositivo para saber que tipo de reportes generar */
    const AlarmasHW = [0x1100, 0x1101, 0x1102];     // Todos los modelos de alarma
    const PuertasHW = [0x2020, 0x2021, 0x2022];     // Todos los modelos de puertas

    let deviceType;
    if(AlarmasHW.includes(device.hwVer))
        deviceType = "Alarma";
    if(PuertasHW.includes(device.hwVer))
        deviceType = "Puertas";
    //console.log(`deviceType (${device.hwVer}): ${deviceType}`);
    
    // Función para recuperar datos extraída para ser reutilizable
    const fetchData = async () => {
        setIsLoading(true);
        try {
            const parametrosData = {
                devId: device.devId,
                fechaDesde: fechaDesde + ' 00:00:00',
                fechaHasta: fechaHasta + ' 23:59:59'
            };

            // Consulta para recuperar registros de la alarma
            if(deviceType === "Alarma") {
                const body = { comando: "CMD_GET_REGISTROS", token: currentUser?.token, data: parametrosData };
                console.log(`[CMD_GET_REGISTROS] body: ${body}`);
                const reg = await crudReportesApi(body);
                const regRows = reg?.result?.rows || [];
                setRegistros(regRows);
                console.log(`useEffect regRows: ${JSON.stringify(regRows)}`);

                // Si hay registros, traemos eventos y configuración
                if (regRows.length > 0) {
                    // Eventos de alarma
                    const bodyEv = { comando: "CMD_GET_EVENTOS", token: currentUser?.token, data: parametrosData };
                    const regEv = await crudReportesApi(bodyEv);
                    setEventos(regEv?.result?.rows || []);

                    /* Buscamos las configuraciones de la alarma */
                    const body2 = { comando: "CMD_GET_ZONA_CONFIG", token: currentUser?.token, data: { devId: device.devId } };
                    const zona = await crudReportesApi(body2);

                    if (zona?.success === true && zona.result?.rows?.length > 0) {
                        const zConfig = JSON.parse(zona.result.rows[0].jsonConfig);
                        //console.log("zConfig: ", zConfig);
                        setZonaConfig(zConfig);
                    }

                    const body3 = { comando: "CMD_GET_SALAC_CONFIG", token: currentUser?.token, data: { devId: device.devId } };
                    const salac = await crudReportesApi(body3);

                    if (salac?.success === true && salac.result?.rows?.length > 0) {
                        const salacConfig = JSON.parse(salac.result.rows[0].jsonConfig);
                        //console.log("salacConfig: ", salacConfig);
                        setSalacConfig(salacConfig);
                    }
                } else {
                    setEventos([]);
                    setZonaConfig([]);
                }
            } else {
                setEventos([]);
                setZonaConfig([]);
            }

            // Consulta para recuperar registros de los accesos (Puertas)
            if(deviceType === "Puertas") {
                const bodyPuertas = { comando: "CMD_GET_REGISTROS_ACCESOS", token: currentUser?.token, data: parametrosData };
                const regP = await crudReportesApi(bodyPuertas);
                const regRowsP = regP?.result?.rows || [];
                setRegistrosAccesos(regRowsP);
                //console.log("useEffect regRowsAccesos: ", regRowsP);

                // Si hay registros, traemos eventos y configuración
                if (regRowsP.length > 0) {
                    // Buscamos las configuraciones de los accesos
                    const body4 = { comando: "CMD_GET_ACCESOS_CONFIG", token: currentUser?.token, data: { devId: device.devId } };
                    const accesos = await crudReportesApi(body4);

                    if (accesos?.success === true && accesos.result?.rows?.length > 0) {
                        const accesosConfig = JSON.parse(accesos.result.rows[0].jsonConfig);
                        //console.log("accesosConfig: ", accesosConfig);
                        setAccesosConfig(accesosConfig);
                    }
                } else {
                    setRegistrosAccesos([]);
                    setAccesosConfig([]);
                }
            } else {
                setRegistrosAccesos([]);
                setAccesosConfig([]);
            }


        } catch (err) {
            //console.log(err.message);
        } finally {
            // Aseguramos que el loading se apague independientemente del resultado
            setIsLoading(false);
        }
    };

    // Efecto inicial (Carga los últimos 7 días por defecto)
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Función para manejar la búsqueda con validación
    const handleBuscar = () => {
        const hoy = dayjs();
        const desde = dayjs(fechaDesde);
        const hasta = dayjs(fechaHasta);
    
        // 1. Validar que ninguna fecha esté en el futuro
        // (usamos 'day' para que permita buscar en el día de hoy, pero no mañana)
        if (desde.isAfter(hoy, 'day') || hasta.isAfter(hoy, 'day')) {
            setErrorFechas("No puedes buscar datos de fechas futuras.");
            return;
        }
    
        const diff = hasta.diff(desde, 'day');
    
        // 2. Validar que el inicio no sea mayor al final
        if (diff < 0) {
            setErrorFechas("La fecha 'Desde' no puede ser mayor a 'Hasta'.");
            return;
        }
    
        // 3. Validar el rango máximo de días
        if (diff > 9) {
            setErrorFechas("El rango de fechas no puede superar 10 días.");
            return;
        }
    
        // Si pasa todas las validaciones
        setErrorFechas(''); 
        fetchData();
    };

    /* Generamos los objetos para los gráficos */

    /* Generamos los datos del último registro */
    const zonasUltimoRegistro = useMemo(() => {
        if (isLoading || !registros?.length || !zonaConfig?.length) return [];
        const zonasUR = procesarTramaZonas(registros[0]);
        return zonasUR.map(zona => {
            const infoExtra = zonaConfig.find(info => info.nZona === zona.numZona);
            if (infoExtra) {
                return { ...zona, ...infoExtra };
            }
            return zona;
        });
    }, [registros, zonaConfig, isLoading]);

    /* Genera el array de eventos añadiendo la descripción del campo acción */
    const arrayEventos = useMemo(() => {
        if (isLoading || !eventos?.length) return [];
        const eventosOrdenados = [...eventos].sort((a, b) => b.id - a.id);

        return eventosOrdenados.map((evento) => ({
            ...evento,
            accionStr: (accionesAlarma[evento.acc] ? accionesAlarma[evento.acc] : "Desconocido"),
            medioStr: (mediosAlarma[evento.medio] ? mediosAlarma[evento.medio] : "Desconocido"),
            estadoStr: (estadosAlarma[evento.aEst] ? estadosAlarma[evento.aEst] : "Desconocido"),
        }));
    }, [eventos, isLoading]);

    
    const seriesZonas = useMemo(() => {
        if(deviceType !== "Alarma") return [];
        if (isLoading || !registros?.length || !zonaConfig?.length) return [];
        return generarSeriesZonas(registros, zonaConfig);
    }, [deviceType, registros, zonaConfig, isLoading]);

    const optionsZonas = useMemo(() => {
        if(deviceType !== "Alarma") return [];
        if (isLoading || !seriesZonas?.length) return {};
        return generarOpcionesZonas(seriesZonas);
    }, [deviceType, seriesZonas, isLoading]);

    const seriesSalac = useMemo(() => {
        if(deviceType !== "Alarma") return [];
        if (isLoading || !registros?.length || !salacConfig?.length) return [];
        return generarSeriesSalac(registros, salacConfig);
    }, [deviceType, registros, salacConfig, isLoading]);

    const optionsSalac = useMemo(() => {
        if(deviceType !== "Alarma") return [];
        if (isLoading || !seriesSalac?.length) return {};
        return generarOpcionesSalac(seriesSalac);
    }, [deviceType, seriesSalac, isLoading]);

    const seriesEstadosAlarma = useMemo(() => {
        if(deviceType !== "Alarma") return [];
        if (isLoading || !registros?.length) return [];
        return generarSerieEstado(registros);
    }, [deviceType, registros, isLoading]);

    const optionsEstadosAlarma = useMemo(() => {
        if(deviceType !== "Alarma") return [];
        if (isLoading || !seriesEstadosAlarma?.length) return {};
        return generarOpcionesEstados(seriesEstadosAlarma);
    }, [deviceType, seriesEstadosAlarma, isLoading]);

    const seriesCombinadas = useMemo(() => {
        if(deviceType !== "Alarma") return [];
        if (isLoading || !registros?.length || !zonaConfig?.length) return [];
        //console.log(`registros: ${registros}\nzonaConfig: ${zonaConfig}`);
        return generarDatosCombinados(registros, zonaConfig);
    }, [deviceType, registros, zonaConfig, isLoading]);

    const optionsCombinadas = useMemo(() => {
        if(deviceType !== "Alarma") return [];
        if (isLoading || !seriesCombinadas?.length) return {};
        //console.log(`registros: ${registros}\nzonaConfig: ${zonaConfig}`);
        return generarOpcionesCombinadas(seriesCombinadas);
    }, [deviceType, seriesCombinadas, isLoading]);


    const seriesPuertas = useMemo(() => {
        if(deviceType !== "Puertas") return [];
        if (isLoading || !registrosAccesos?.length || !accesosConfig?.length) return [];
        return generarSeriesPuertas(registrosAccesos, accesosConfig);
    }, [deviceType, registrosAccesos, accesosConfig, isLoading]);

    const optionsPuertas = useMemo(() => {
        if(deviceType !== "Puertas") return [];
        if (isLoading || !seriesPuertas?.length) return {};
        return generarOpcionesPuertas(seriesPuertas);
    }, [deviceType, seriesPuertas, isLoading]);


    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <Stack direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
            spacing={0}
            justifyContent={{ lg: 'center' }}
            flexGrow={1} pt={2} pb={2}
        >
            <Box bgcolor='white' borderRight={1} borderColor='lightgray' flex={3} marginRight={0} padding={2} style={{ width: '40%', borderRadius: 0 }} >
                <Typography variant="h6" component="h6" mt={2} pb={1} align='left'>
                    Datos del Dispositivo
                </Typography>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 350 }} size="small" aria-label="a dense table">
                        <TableBody>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>Tipo Dispositivo:</strong></TableCell>
                                <TableCell align="left">{deviceType}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>Id Dispositivo:</strong></TableCell>
                                <TableCell align="left">{device.devId}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>Descripción:</strong></TableCell>
                                <TableCell align="left">{device.descripcion}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>IMEI:</strong></TableCell>
                                <TableCell align="left">{device.imei}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>Celular Dispositivo:</strong></TableCell>
                                <TableCell align="left">{device.devCel}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>MAC Address:</strong></TableCell>
                                <TableCell align="left">{device.macAdd}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>Comentarios:</strong></TableCell>
                                <TableCell align="left">{device.comments}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography variant="h6" component="h6" mt={4} pb={1} align='left'>
                    Datos del Usuario
                </Typography>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 350 }} size="small" aria-label="a dense table">
                        <TableBody>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>Usuario Alarma:</strong></TableCell>
                                <TableCell align="left">{device?.devUserName || "---"}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>Celular Usuario:</strong></TableCell>
                                <TableCell align="left">{formatearCelular(device.devUserCel)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="left" sx={{ width: 180 }}><strong>Email:</strong></TableCell>
                                <TableCell align="left">{device?.devUserEmail || "---"}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* --- SECCIÓN DE FILTRO POR FECHAS --- */}
                <Typography variant="h6" component="h6" mt={2} pt={2} pb={1} align='left'>
                    Filtros Reporte
                </Typography>
                <Box mb={3} p={2} component={Paper} elevation={1} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">Fechas (Máximo 7 días)</Typography>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            label="Desde"
                            type="date"
                            size="small"
                            value={fechaDesde}
                            onChange={(e) => setFechaDesde(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            label="Hasta"
                            type="date"
                            size="small"
                            value={fechaHasta}
                            onChange={(e) => setFechaHasta(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                    <Button
                        variant="contained"
                        onClick={handleBuscar}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Cargando...' : 'Buscar'}
                    </Button>
                    {errorFechas && (
                        <Typography color="error" variant="body2">
                            {errorFechas}
                        </Typography>
                    )}
                </Box>
                {/* ------------------------------------ */}
            </Box>

            <Box flex={9} sx={{ width: '60%', typography: 'body1', p: 2 }}>

                <TabContext value={value}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList onChange={handleChange} aria-label="lab API tabs example">
                            {deviceType === "Alarma" && (<Tab label="Último Registro" value="1" />)}
                            {deviceType === "Alarma" && (<Tab label="Eventos" value="2" />)}
                            {deviceType === "Alarma" && (<Tab label="Movimiento Zonas" value="3" />)}
                            {deviceType === "Alarma" && (<Tab label="Estados Alarma" value="4" />)}
                            {deviceType === "Alarma" && (<Tab label="Gráfico Completo" value="5" />)}
                            {deviceType === "Puertas" && (<Tab label="Gráfico Accesos" value="1" />)}
                            {(deviceType === "Alarma" && currentUser.tipoUsuario === 'superusuario') && (<Tab label="Parámetros Gráfico" value="6" />)}
                        </TabList>
                    </Box>

                    {/* Tab Ultimo registro */}
                    {deviceType === "Alarma" && (
                        <TabPanel value="1">
                            {
                                (registros.length > 0 && zonasUltimoRegistro?.length > 0) ?
                                    <>
                                        <Typography variant="h6" component="h6" mt={1} pt={1} align='left'>
                                            Estado Alarma: {registros.length > 0 ? CE_ESTADOS_ALARMA[registros[0].aEst] : "Sin Datos"}
                                        </Typography>
                                        <Typography variant="p" component="p" mb={1} pb={1} align='left'>
                                            Fecha: {registros.length > 0 ?
                                                dayjs(registros[0].date).tz("America/La_Paz").format("DD/MM/YYYY HH:mm:ss")
                                                : "Sin Datos"}
                                        </Typography>
                                        <TableContainer component={Paper}>
                                            <Table sx={{ minWidth: 350 }} size="small" aria-label="a dense table">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell align="center" sx={{ width: 60 }}>Zona</TableCell>
                                                        <TableCell align="center" sx={{ width: 180 }}>Descripción</TableCell>
                                                        <TableCell align="center" sx={{ width: 100 }}>Habilitación</TableCell>
                                                        <TableCell align="center" sx={{ width: 100 }}>Estado</TableCell>
                                                        <TableCell align="center" sx={{ width: 80 }}>Movimiento</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {zonasUltimoRegistro.map((zona, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell align="center" sx={{ width: 60 }}> {zona.numZona + 1} </TableCell>
                                                            <TableCell align="center" sx={{ width: 120 }}> {zona.desLarga} </TableCell>
                                                            <TableCell align="center" sx={{ width: 100 }}> {CE_HABILITACIONES_ZONA[zona.hab]} </TableCell>
                                                            <TableCell align="center" sx={{ width: 100 }}> {CE_ESTADOS_ZONA[zona.estadoZona]} </TableCell>
                                                            <TableCell align="center" sx={{ width: 80 }}> {zona.cuentaParcial} </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                    :
                                    <Typography variant="h6" component="h6" mt={2} pb={1} align='left'>
                                        No se recupero ninguna información
                                    </Typography>
                            }
                        </TabPanel>
                    )}

                    {/* Tab Eventos */}
                    {deviceType === "Alarma" && (
                        <TabPanel value="2">
                            {
                                (arrayEventos.length > 0) ?
                                    <>
                                        <Typography variant="h6" component="h6" mt={2} pb={1} align='left'>
                                            Últimos Eventos
                                        </Typography>
                                        <TableContainer component={Paper}>
                                            <Table sx={{ minWidth: 350 }} size="small" aria-label="a dense table">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: "#EEE" }}>
                                                        <TableCell align="center" sx={{ width: 60 }}>Num</TableCell>
                                                        <TableCell align="center" sx={{ width: 180 }}>Usuario</TableCell>
                                                        <TableCell align="center" sx={{ width: 100 }}>Evento</TableCell>
                                                        <TableCell align="center" sx={{ width: 120 }}>Estado</TableCell>
                                                        <TableCell align="center" sx={{ width: 100 }}>Medio</TableCell>
                                                        <TableCell align="center" sx={{ width: 120 }}>Fecha</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {arrayEventos.map((ev, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell align="center" sx={{ width: 60 }}> {index + 1} </TableCell>
                                                            <TableCell align="center" sx={{ width: 180 }}> {ev.name} </TableCell>
                                                            <TableCell align="center" sx={{ width: 100 }}> {ev.accionStr} </TableCell>
                                                            <TableCell align="center" sx={{ width: 120 }}> {ev.estadoStr} </TableCell>
                                                            <TableCell align="center" sx={{ width: 100 }}> {ev.medioStr} </TableCell>
                                                            <TableCell align="center" sx={{ width: 120 }}> {formatearFecha(ev.date)} </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </>
                                    :
                                    <Typography variant="h6" component="h6" mt={2} pb={1} align='left'>
                                        No se recuperaron eventos
                                    </Typography>
                            }
                        </TabPanel>
                    )}

                    {/* Tab Movimiento Zonas - Graf ApexCharts */}
                    {deviceType === "Alarma" && (
                        <TabPanel value="3">
                            {!isLoading && optionsZonas && seriesZonas.length > 0 && (
                                <Chart
                                    options={optionsZonas}
                                    series={seriesZonas}
                                    type="area"
                                    height={350}
                                />
                            )}
                            {!isLoading && optionsSalac && seriesSalac.length > 0 && (
                                <div style={{ marginTop: '30px' }}>
                                    <Chart
                                        options={optionsSalac}
                                        series={seriesSalac}
                                        type="area"
                                        height={200}
                                    />
                                </div>
                            )}

                        </TabPanel>
                    )}

                    {/* Tab Estados Alarma - Graf ApexCharts */}
                    {deviceType === "Alarma" && (
                        <TabPanel value="4">
                            {!isLoading && optionsEstadosAlarma && seriesEstadosAlarma.length > 0 && (
                                <Chart
                                    options={optionsEstadosAlarma}
                                    series={seriesEstadosAlarma}
                                    type="line"
                                    height={350}
                                />
                            )}
                        </TabPanel>
                    )}

                    {/* Tab Gráfico Completo - Graf ApexCharts */}
                    {deviceType === "Alarma" && (
                        <TabPanel value="5">
                            {!isLoading && optionsCombinadas && seriesCombinadas.length > 0 && (
                                <Chart
                                    options={optionsCombinadas}
                                    series={seriesCombinadas}
                                    type="area"
                                    height={350}
                                />
                            )}
                        </TabPanel>
                    )}

                    {/* Tab Gráfico accesos - Graf ApexCharts */}
                    {deviceType === "Puertas" && (
                        <TabPanel value="1">
                            {!isLoading && optionsPuertas && seriesPuertas.length > 0 && (
                                <Chart
                                    options={optionsPuertas}
                                    series={seriesPuertas}
                                    type="area"
                                    height={350}
                                />
                            )}
                        </TabPanel>
                    )}

                    {/* Tab Parámetros Gráfico */}
                    {(deviceType === "Alarma" && currentUser.tipoUsuario === 'superusuario') && (
                        <TabPanel value="6">
                            <Box sx={{ width: '100%', height: 300 }}>
                                <hr />
                                <Box><strong>Descripción del string Zonas</strong></Box>
                                <Box marginLeft={8}>
                                    <li>Parámetro 0: numZona</li>
                                    <li>Parámetro 1: estadoZona</li>
                                    <li>Parámetro 2: estadoBinario</li>
                                    <li>Parámetro 3: timeAnormal</li>
                                    <li>Parámetro 4: cuentaParcial</li>
                                    <li>Parámetro 5: cuentaTotal</li>
                                </Box>
                                <hr />
                                <Typography variant="h6" component="h6" mt={2} pb={1} align='left'>
                                    Datos por registro (Registro zonas)
                                </Typography>
                                {registros.length > 0 ?
                                    Object.entries(registros[0]).map(([key, value]) => (
                                        <li key={key}>
                                            <strong>{key}:</strong> {String(value)}
                                        </li>
                                    ))
                                    :
                                    <Box>Sin Registros</Box>
                                }
                                <hr />
                                <Typography variant="h6" component="h6" mt={2} pb={1} align='left'>
                                    Datos por registro (Eventos)
                                </Typography>
                                {arrayEventos.length > 0 ?
                                    Object.entries(arrayEventos[0]).map(([key, value]) => (
                                        <li key={key}>
                                            <strong>{key}:</strong> {String(value)}
                                        </li>
                                    ))
                                    :
                                    <Box>Sin Registros</Box>
                                }
                            </Box>
                        </TabPanel>
                    )}

                </TabContext>
            </Box>
        </Stack>
    )
}

export default ReporteEstado;